import { createLensRegistry } from "./lensRegistry.js";
import ScopeOverviewBody from "../../components/lenses/ScopeOverviewBody.jsx";
import SignalsConsoleBody from "../../components/lenses/SignalsConsoleBody.jsx";
import SignalDetailBody from "../../components/lenses/SignalDetailBody.jsx";
import EndpointSignalDetailBody from "../../components/lenses/EndpointSignalDetailBody.jsx";
import SkillDetailBody from "../../components/lenses/SkillDetailBody.jsx";
import MemoryDetailBody from "../../components/lenses/MemoryDetailBody.jsx";
import MemoryStoreLens from "../../components/lenses/MemoryStoreLens.jsx";
import McpServerDetailBody from "../../components/lenses/McpServerDetailBody.jsx";
import ToolDetailBody from "../../components/lenses/ToolDetailBody.jsx";
import InventoryBody from "../../components/lenses/InventoryBody.jsx";
import DimensionInventoryBody from "../../components/lenses/DimensionInventoryBody.jsx";
import XRayBody from "../../components/lenses/XRayBody.jsx";
import CanvasesBody from "../../components/lenses/CanvasesBody.jsx";
import ScopeAnatomyBody from "../../components/lenses/ScopeAnatomyBody.jsx";
import ReviewsBody from "../../components/lenses/ReviewsBody.jsx";
import ClusterAnatomyBody from "../../components/lenses/ClusterAnatomyBody.jsx";
import { subjectForCommand } from "../signalCatalog.js";

// The app's lens registry singleton. Lenses register here; LensHost resolves
// against it. Phase 1: only the scope-overview fallback. Dimension/value
// lenses (the signals alert console, skills content-compare) register here as
// they land.
export const appLenses = createLensRegistry();

// Scope overview — the universal fallback (event-centric: hero + activity
// surface + trace/exec log). Its surface descriptor mirrors ActivityChart's
// built-in lens options for now; wiring the descriptor to actually DRIVE the
// chart's tabs (replacing ActivityChart's internal pickLensOptions) is a small
// follow-on kept separate so this reseat stays behavior-preserving.
const SCOPE_OVERVIEW_SURFACE = {
  tabs: [
    { value: "endpoints", label: "Active endpoints" },
    { value: "activity", label: "Active sessions" },
    { value: "prompts", label: "Prompt volume" },
    { value: "tokens", label: "Token volume" },
    { value: "clusters", label: "Topic swimlane", kind: "swimlane" },
  ],
  defaultTab: "endpoints",
};

appLenses.register({
  id: "scope.overview",
  title: "Overview",
  level: "scope",
  surface: SCOPE_OVERVIEW_SURFACE,
  Body: ScopeOverviewBody,
});

// X-ray — a UNIVERSAL scope lens (available at every scope as a toggle
// beside the overview; the overview stays the default because it
// registered first): what is this slice of traffic actually about?
// Term co-occurrence map + run-gated LLM description over the window's
// prompt summaries.
appLenses.register({
  id: "scope.xray",
  title: "X-ray",
  level: "scope",
  surface: null,
  Body: XRayBody,
});

// Anatomy — a UNIVERSAL scope lens: the makeup of this slice of traffic.
// "Where you are" (the paged position stack — you at each level of the drill) +
// "who/what drives it" (composition cross-tab with verdict + baseline lift +
// correlate). Uniform across EVERY scope and dim including clusters (no
// override) and the tenant root. No defaultActive: the overview stays the
// default click target, Anatomy rides as a toggle. Registered here (before
// Inventory) so it sits directly above Inventory in the sidebar VIEWS list —
// registration order is sidebar order.
appLenses.register({
  id: "scope.anatomy",
  title: "Anatomy",
  level: "scope",
  surface: null,
  Body: ScopeAnatomyBody,
});

// Reviews -- a UNIVERSAL scope lens: which conversations in this slice of
// traffic has the trace-review pipeline flagged, and what did it find in each.
// The trace log's table at its session grain, over the sessions that have had
// a finding land on them, newest review first, with a timeline of each
// session's flagged reviews where the log's model column would be; a row
// opens its reviews beneath it, each with the turns it judged and a mark per
// finding. Scope resolves through the sessions in it, so every dim the sidebar
// can drill is covered (see lib/scopeReviews). Registered here so it sits
// between Anatomy and Canvases in the sidebar VIEWS list; no defaultActive,
// the overview stays the default click target.
appLenses.register({
  id: "scope.reviews",
  title: "Reviews",
  level: "scope",
  surface: null,
  Body: ReviewsBody,
});

// Inventory is reached by opening a dimension folder (its "All <dim>" board),
// not from the universal VIEWS list. Endpoints keeps its bespoke rich body;
// every other regular dimension gets a basic inventory table through the
// dimension-default fallback. Both are `dimensionOnly`: focusing the dimension
// opens the inventory, while selecting one value keeps that value's overview.
appLenses.register({
  id: "dimension.endpoints",
  title: "Inventory",
  defaultActive: true,
  dimensionOnly: true,
  level: "dimension",
  dim: "endpoints",
  surface: null,
  Body: InventoryBody,
});
appLenses.register({
  id: "dimension.inventory",
  title: "Inventory",
  defaultActive: true,
  dimensionOnly: true,
  level: "dimension",
  // no `dim` → registered as store.dimensionDefault (the member-rollup fallback).
  surface: null,
  Body: DimensionInventoryBody,
});

// Canvases — a universal scope lens (registers after the others so it lands
// last in the sidebar VIEWS list, beside Overview / X-ray / Anatomy; the
// overview stays default). Renders the read-only built-in canvases (+ any the tenant saved or
// forked) at the current drilldown level: each scope-aware kernel re-runs
// narrowed to the active scope. See CanvasesBody + lib/builtins/builtinCanvases.
appLenses.register({
  id: "scope.canvases",
  title: "Canvases",
  level: "scope",
  surface: null,
  Body: CanvasesBody,
});

// Signals dimension lens — the "alert console" (focusDim: "signals").
// Member-centric: aggregates across all defined signals for the window via
// KernelBlock, no backend. surface is null for v1 — a firing-density swimlane
// is a follow-on.
appLenses.register({
  id: "dimension.signals",
  title: "Alert console",
  defaultActive: true,
  level: "dimension",
  dim: "signals",
  surface: null,
  Body: SignalsConsoleBody,
  // Signals are client-side, windowed scans — the universal scope lenses
  // (Overview / X-ray / Inventory) assume a server-joinable scope and don't
  // work meaningfully here yet, so suppress them. Revisit once signals are a
  // server-side join (O-2145). See lensRegistry resolveLenses.
  solo: true,
});

// Signals VALUE lens, ENDPOINT-SUBJECT variant — registered before the general
// one so its `when` is what resolveLenses tries first (a matching `when` beats a
// when-less value lens regardless of order, but reading order should match
// precedence). A signal whose tags annotate an ENDPOINT has no firing prompts to
// list, so the prompt body below would render an empty table; this one lists the
// endpoints instead and opens their config in the right panel.
//
// `subjectForCommand` is a static overlay lookup, NOT a catalog read — `when`
// runs synchronously during lens resolution, so it must answer before (and
// without) signal-catalog hydration or a deeplink would land on the wrong body
// for a frame.
appLenses.register({
  id: "value.signals.endpoint",
  title: "Detail",
  defaultActive: true,
  level: "value",
  dim: "signals",
  surface: null,
  when: (step) => subjectForCommand(step?.ids?.[0] ?? step?.id) === "endpoint",
  Body: EndpointSignalDetailBody,
  solo: true,
});

// Signals VALUE lens — the detail view for one selected signal. Wins over the
// dimension-lens fallback when a specific signal is selected (resolver checks
// value lenses first), so clicking a signal shows ITS results rather than the
// aggregate console.
appLenses.register({
  id: "value.signals",
  title: "Detail",
  defaultActive: true,
  level: "value",
  dim: "signals",
  surface: null,
  Body: SignalDetailBody,
  // No Overview / X-ray / Inventory companions for a signal value (see the
  // dimension.signals note) — just Detail until signals are server-side.
  solo: true,
});

// Skills VALUE lens — clicking a skill extracts its injected SKILL.md body from
// the skill's scoped prompts and lists distinct versions (drift) as rows that
// open in the detail sidebar. (No skills dimension/aggregate lens — selecting a
// skill goes straight to its detail.)
appLenses.register({
  id: "value.skills",
  title: "Detail",
  level: "value",
  dim: "skills",
  surface: null,
  Body: SkillDetailBody,
});

// Memories DIMENSION lens — the store overview. Rendered when the Memories dim
// is FOCUSED with no committed value (focusDim === "memories"), i.e. the moment
// you open the section. Scope-aware (memory is per-person): scoped to a person
// it's a flat, scannable list of every memory file they track across all their
// projects; at the tenant level it's a roster of people to scope into first.
// Registering this is also what makes the sidebar focus the dim on open
// (ScopeSidebar gates onFocusDim on a registered dimension lens). See
// MemoryStoreLens.jsx.
appLenses.register({
  id: "dimension.memories",
  title: "Memory store",
  defaultActive: true,
  level: "dimension",
  dim: "memories",
  surface: null,
  Body: MemoryStoreLens,
});

// Memories VALUE lens — clicking a memory extracts its actual file CONTENT
// (the Read/Write of `…/memory/<slug>.md`) from the memory's scoped prompts and
// lists distinct versions (drift — memories get rewritten over time) as rows
// that open the full content in the detail sidebar. The direct analog of the
// skills Detail lens; see MemoryDetailBody.jsx + memoryBodies.js.
appLenses.register({
  id: "value.memories",
  title: "Detail",
  // Selecting a memory lands on its CONTENT, not a generic trace overview — a
  // project node opens its store map, a file its version drift. defaultActive so
  // it's the default for a memory value; explicit `?view=` deeplinks still win.
  // See defaultLensOf.
  defaultActive: true,
  level: "value",
  dim: "memories",
  surface: null,
  Body: MemoryDetailBody,
});

// MCP servers VALUE lens — "Detail", analogous to the cluster Detail (no
// defaultActive: the scope overview stays the default for a server click, the
// detail rides along as a toggle). An MCP server is a bundle of tools, so the
// body fuses the skill drift template (scan the window, extract + version the
// server's declared tool surface) with the cluster composition template (who
// calls it, concentration verdict), plus a per-tool usage roster and the
// recent calls list. See McpServerDetailBody.jsx.
appLenses.register({
  id: "value.mcp_servers",
  title: "Detail",
  level: "value",
  dim: "mcp_servers",
  surface: null,
  Body: McpServerDetailBody,
});

// Tools VALUE lens — "Detail" (no defaultActive, like the others: overview
// stays the default click target, detail rides as a toggle). Per-tool view:
// the tool's declared definition + drift (skill template), the actual call
// arguments it was invoked with, and who calls it (cluster composition). MCP
// tools (mcp__<server>__<tool>) link up to their server. See ToolDetailBody.jsx.
appLenses.register({
  id: "value.tools",
  title: "Detail",
  level: "value",
  dim: "tools",
  surface: null,
  Body: ToolDetailBody,
});

// Clusters VALUE lens — "Detail": the WHOLE cluster taxonomy as an icicle map
// with this cluster pinned inside it + cluster-specific stats (share of level,
// leaves) + composition. Separate from the universal Anatomy lens (which treats
// clusters like any other dim) — clusters get both Detail (the hierarchy view)
// and Anatomy. No defaultActive: overview stays the default cluster click target.
appLenses.register({
  id: "value.clusters",
  title: "Detail",
  level: "value",
  dim: "clusters",
  surface: null,
  Body: ClusterAnatomyBody,
});
